// One PHI rule for "can this block be picked today?" — the Farm Map's Spray
// safety overlay (FM01 F1), and the rule FM10 F1 writes again in SQL for the
// compliance rollup. One case table pins both (phiCases.ts).
//
// The rule, in the Harvest Picks advisor's order (phiSafeToHarvest.ts):
//   - a spray's PHI is its record's own `pre_harvest_interval_days` — the
//     record wins over the label (Jason, 4 Oct 2026: an operator sets a line's
//     PHI when the label's default is for another crop). A recorded 0 is a
//     real zero-day interval, never unknown.
//   - with no record PHI, each product in the tank (the header chemical and
//     every live mix line) gives its LABEL PHI, and the spray is held by the
//     longest, so a tank mix is held by its slowest product. An adjuvant or
//     nutrient (or a fertiliser) with no label PHI withholds nothing (0). Any
//     other product with none — a pesticide, a free-typed product, one the
//     reader can't see — leaves the spray UNKNOWN as well (Jason, 4 Oct
//     2026), and so does a spray with no product at all.
//   - a spray can be both: held by its known products AND unknown. It holds
//     while the known hold runs; after that it is unknown, never clear.
//   - the hold is active while the clear date (applied + PHI days) is after
//     today; harvest is allowed ON the clear date.
//   - per block: an active hold beats everything; else an unknown PHI beats
//     cleared — an unknown is never shown as "Cleared — safe to harvest".
//
// The map has no pick date, so it answers for today, and a spray dated after
// today still counts: a mis-dated record must never clear a block.

import { addDaysYmd } from '@/lib/dates'
import { ageDays } from '@/lib/growerStatement'

export interface PhiSprayFacts {
  spray_id:  string
  block_id:  string
  applied:   string | null          // YYYY-MM-DD
  /** spray_records.pre_harvest_interval_days — the snapshot taken at completion. */
  recordPhi: number | null
  /** Each product's label PHI for the rule (labelPhiOf): the header product,
   *  when the record names one, and each live mix line. null = unknown. */
  labelPhis: ReadonlyArray<number | null>
  product:   string | null
}

export type PhiBlockVerdict =
  | { state: 'unsafe'; days: number; clearsOn: string }
  | { state: 'unknown'; product: string | null; appliedAt: string; more: number }
  | { state: 'safe' }

/** The spray_records columns the rule reads: the record's PHI, and for the
 *  header product and every mix line the label PHI plus what kind of product
 *  it is (a deleted line is dropped in sprayLabelPhis). Embed it under
 *  `spray_records` or select it directly. */
const PHI_CHEMICAL = 'pre_harvest_interval_days, spray_type, product_class'
export const PHI_SPRAY_COLUMNS =
  'applied_at, pre_harvest_interval_days, product_name_override, chemical_id, ' +
  `chemicals:chemical_id ( product_name, ${PHI_CHEMICAL} ), ` +
  `spray_record_chemicals ( deleted_at, chemical_id, chemicals:chemical_id ( ${PHI_CHEMICAL} ) )`

export interface PhiLabelChemical {
  pre_harvest_interval_days: number | null
  spray_type?:               string | null
  product_class?:            string | null
}

/** The rows the rule reads the labels from — a spray record, or the parent
 *  embedded through a spray_block_targets junction row. */
export interface PhiLabelRow {
  chemical_id?:            string | null
  chemicals:               PhiLabelChemical | null
  spray_record_chemicals?: Array<{ deleted_at: string | null; chemical_id?: string | null; chemicals: PhiLabelChemical | null }> | null
}

export interface PhiSprayRead extends PhiLabelRow {
  spray_id:                  string
  applied_at:                string | null
  pre_harvest_interval_days: number | null
  product_name_override:     string | null
  chemicals:                 (PhiLabelChemical & { product_name: string | null }) | null
}

const WETTER_SPRAY_TYPES = new Set(['ADJUVANT', 'NUTRIENT'])

/** One product's label PHI for the rule: its own; 0 for an adjuvant, nutrient
 *  or fertiliser that has none (it withholds nothing); null (unknown) for any
 *  other product without one — and for a free-typed product or one the reader
 *  cannot see (no chemicals row). */
export function labelPhiOf(c: PhiLabelChemical | null | undefined): number | null {
  if (!c) return null
  if (c.pre_harvest_interval_days != null) return c.pre_harvest_interval_days
  return WETTER_SPRAY_TYPES.has(c.spray_type ?? '') || c.product_class === 'FERTILISER' ? 0 : null
}

/** A spray's products' label PHIs: the header product when the record names
 *  one (chemical_id), then every live mix line — a line with no chemical_id is
 *  a free-typed product, so it reads unknown. */
export function sprayLabelPhis(r: PhiLabelRow): Array<number | null> {
  const out: Array<number | null> = []
  if (r.chemical_id != null) out.push(labelPhiOf(r.chemicals))
  for (const l of r.spray_record_chemicals ?? []) if (!l.deleted_at) out.push(labelPhiOf(l.chemicals))
  return out
}

/** One spray row, read with PHI_SPRAY_COLUMNS, as the rule's input for one block. */
export function phiSprayFacts(blockId: string, r: PhiSprayRead): PhiSprayFacts {
  return {
    spray_id:  r.spray_id,
    block_id:  blockId,
    applied:   r.applied_at,
    recordPhi: r.pre_harvest_interval_days,
    labelPhis: sprayLabelPhis(r),
    product:   r.product_name_override ?? r.chemicals?.product_name ?? null,
  }
}

/** A spray's PHI: `days`, the known hold (the record's, else the longest
 *  label PHI; null when nothing is known) and `unknown`, true when the record
 *  has none and some product's label is unknown (or there is no product). */
export function sprayPhi(recordPhi: number | null, labelPhis: ReadonlyArray<number | null>): { days: number | null; unknown: boolean } {
  if (recordPhi != null) return { days: recordPhi, unknown: false }
  let days: number | null = null
  for (const d of labelPhis) if (d != null && (days == null || d > days)) days = d
  return { days, unknown: labelPhis.length === 0 || labelPhis.some(d => d == null) }
}

/** A spray today: the date its KNOWN hold clears (null when nothing holds
 *  it today) and whether its PHI is partly or wholly unknown. */
function sprayOutcome(applied: string, s: PhiSprayFacts, today: string): { clearsOn: string | null; unknown: boolean } {
  const { days, unknown } = sprayPhi(s.recordPhi, s.labelPhis)
  const clearsOn = days != null && days > 0 ? addDaysYmd(applied, days) : null
  return { clearsOn: clearsOn != null && clearsOn > today ? clearsOn : null, unknown }
}

interface BlockTally {
  latestClear: string | null
  unknown:     Array<{ product: string | null; applied: string }>
}

function blockVerdict(t: BlockTally, today: string): PhiBlockVerdict {
  if (t.latestClear) return { state: 'unsafe', days: ageDays(today, t.latestClear), clearsOn: t.latestClear }
  if (t.unknown.length === 0) return { state: 'safe' }
  const newest = [...t.unknown].sort((a, b) => b.applied.localeCompare(a.applied))[0]
  return { state: 'unknown', product: newest.product, appliedAt: newest.applied, more: t.unknown.length - 1 }
}

/** How far back a spray with no PHI still makes a block "unknown" — the map's
 *  window, and the compliance rollup's (`applied_at >= p_today - 120`). */
export const PHI_UNKNOWN_LOOKBACK_DAYS = 120

/** Per-block verdicts for today. A block with no spray in the input is absent.
 *  `unknownSince` drops a no-PHI spray applied before it (the map's query is
 *  already windowed; a caller reading further back for active holds passes
 *  addDaysYmd(today, -PHI_UNKNOWN_LOOKBACK_DAYS)). */
export function phiBlockStates(
  sprays: ReadonlyArray<PhiSprayFacts>,
  today: string,
  opts: { unknownSince?: string } = {},
): Record<string, PhiBlockVerdict> {
  // The primary block is also a junction row (mig 281), so one spray can
  // arrive twice for a block; count it once.
  const seen = new Set<string>()
  const tally = new Map<string, BlockTally>()
  for (const s of sprays) {
    const key = `${s.block_id}|${s.spray_id}`
    if (!s.applied || seen.has(key)) continue
    seen.add(key)
    const t = tally.get(s.block_id) ?? { latestClear: null, unknown: [] }
    tally.set(s.block_id, t)
    const o = sprayOutcome(s.applied, s, today)
    // unknownSince bounds only the unknown half: a known hold is never dropped.
    if (o.unknown && !(opts.unknownSince && s.applied < opts.unknownSince)) t.unknown.push({ product: s.product, applied: s.applied })
    if (o.clearsOn && (!t.latestClear || o.clearsOn > t.latestClear)) t.latestClear = o.clearsOn
  }
  const out: Record<string, PhiBlockVerdict> = {}
  for (const [block, t] of tally) out[block] = blockVerdict(t, today)
  return out
}
